import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Check, Clock, X, List, CalendarDays, RefreshCw, Pencil, Trash2, FileBarChart2, ClipboardList, FileText, Archive, ChevronDown, Image as ImageIcon, Send, Link2, Copy as CopyIcon, FileSignature, FileDown } from 'lucide-react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { ListSkeleton, EmptyState } from '../components/ui';
import ClientePicker from '../components/ClientePicker';
import MediaManager from '../components/MediaManager';
import PedCalendario from '../components/PedCalendario';
import { PostingBadge, PostingGuardrail, POSTING_STYLE } from '../components/PostingGuardrail';
import { useAuth } from '../context/AuthContext';
import { can, RUOLI_SOCIAL } from '../lib/ruoli';
import RubricheModal from '../components/RubricheModal';
import VoceBrandModal from '../components/VoceBrandModal';
import { Repeat, Eye, Settings2 } from 'lucide-react';
import MoreMenu from '../components/MoreMenu';
import { FormatTag, Thumb, giornoLabel, SegnaliContenuto } from '../components/content';
import { useIsMobile } from '../hooks/useResponsive';
import { generatePedPDF } from '../utils/pedPDF';
import { useClienteAttivo, ordinaClienti, clienteValido } from '../hooks/useClienteAttivo';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import CoperturaContratto, { BadgeCopertura } from '../components/CoperturaContratto';

// Metrica "principale" di un post pubblicato, per la lista (la prima disponibile).
const METRICA = [['reach', 'reach'], ['impressions', 'impr.'], ['views', 'views'], ['likes', 'like']];
function fmtK(n) { n = Number(n) || 0; return n >= 10000 ? `${Math.round(n / 1000)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n); }
function InsightsMini({ ins }) {
  if (!ins) return null;
  const m = METRICA.find(([k]) => ins[k] != null && Number(ins[k]) > 0);
  if (!m) return null;
  const extra = ins.saved != null ? ` · ${fmtK(ins.saved)} salv.` : ins.likes != null && m[0] !== 'likes' ? ` · ${fmtK(ins.likes)} like` : '';
  return (
    <span title={Object.entries(ins).filter(([k]) => k !== 'on').map(([k, v]) => `${k}: ${v}`).join('\n')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, color: 'var(--cyan)', whiteSpace: 'nowrap' }}>
      <Eye size={11} /> {fmtK(ins[m[0]])} {m[1]}{extra}
    </span>
  );
}

const MESI_LBL = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

const STATO_STYLE = {
  'Idea':         { bg:'rgba(148,163,184,0.12)', color:'#94a3b8' },
  'In Scrittura': { bg:'var(--yellow-dim)', color:'var(--yellow)' },
  'In Design':    { bg:'var(--purple-dim)', color:'var(--purple)' },
  'In Review':    { bg:'rgba(59,130,246,0.12)', color:'#60a5fa' },
  'Approvato':    { bg:'var(--green-dim)', color:'var(--green)' },
  'Pubblicato':   { bg:'var(--green-dim)', color:'var(--green)' },
  'Scartato':     { bg:'var(--red-dim)', color:'var(--red)' },
};

function StatoBadge({ stato }) {
  const s = STATO_STYLE[stato] || STATO_STYLE['Idea'];
  return (
    <span style={{ fontSize:11.5, padding:'3px 9px', borderRadius:999, background:s.bg, color:s.color, fontWeight:600, whiteSpace:'nowrap' }}>
      {stato}
    </span>
  );
}

// Fasi editoriali (produzione): finché la riga è qui, mostriamo lo stato editoriale.
// Da 'Approvato'/'Pubblicato' in poi comanda lo stato di POSTING (pronta/programmata/…).
// Così ogni riga ha UN SOLO badge coerente, mai due che sembrano contraddirsi.
const PRODUZIONE = ['Idea', 'In Scrittura', 'In Design', 'In Review', 'Scartato'];

// Colonne della tabella (condivise da header e righe). Data+ora affiancate → righe più basse.
// La colonna Ads compare solo se almeno un contenuto ha dati di campagna.
const colsFor = (ads) => `84px minmax(170px,2fr) 100px 228px 158px minmax(150px,1.25fr) ${ads ? '84px ' : ''}104px`;
const headersFor = (ads) => ['ID', 'Titolo / Hook', 'Formato', 'Data · ora', 'Copy · Visual', 'Stato', ...(ads ? ['Ads'] : []), 'Azioni'];
const minWFor = (ads) => (ads ? 1130 : 1040);

// Stato di posting: badge + barra guardrail sono nel componente condiviso
// components/PostingGuardrail.js (usato identico da PED e PED Storie).


export default function PED() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { success, error } = useToast();
  const { user } = useAuth();
  const puoPubblicare = can(user, RUOLI_SOCIAL);
  const [rubricheOpen, setRubricheOpen] = useState(false);
  const [voceOpen, setVoceOpen] = useState(false);

  const [clienti, setClienti] = useState([]);
  const [selectedCliente, setSelectedCliente] = useClienteAttivo();
  const clienteSel = clienti.find(c => c.id === selectedCliente);
  const clienteNome = clienteSel?.nome || '';
  const clienteAzienda = clienteSel?.azienda || clienteSel?.nome || '';
  const [meseSel, setMeseSel] = useState(''); // '' = tutti i futuri | 'YYYY-MM' = mese scelto
  const [vaultFiles, setVaultFiles] = useState(null);
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState('lista'); // 'lista' | 'calendario'
  const [sezione, setSezione] = useState('programma'); // 'programma' (futuri) | 'archivio' (passati)
  const [mesiChiusi, setMesiChiusi] = useState(() => new Set()); // mesi collassati in archivio
  const [mediaFor, setMediaFor] = useState(null); // riga con il gestore media aperto

  // Carichiamo TUTTE le righe-contenuto del cliente (no storie, no campagne ADS →
  // queste vivono nelle tab dedicate) e partizioniamo in JS: futuri → "In programma",
  // passati → "Archivio" (catalogati per mese).
  const pedQuery = () => `/ped?cliente_id=${selectedCliente}&escludi_formato=Storia&escludi_campagne=1`;

  useEffect(() => {
    api.get('/clienti').then(r => {
      const lista = ordinaClienti(r.data || []);
      setClienti(lista);
      setSelectedCliente(clienteValido(lista, selectedCliente));
    }).catch(()=>{});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCliente) { setRighe([]); return; }
    setLoading(true);
    api.get(pedQuery())
      .then(r => { setRighe(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCliente]);

  // Carica i file vault per il mese selezionato (o il mese corrente se "Tutti").
  useEffect(() => {
    if (!clienteAzienda) { setVaultFiles(null); return; }
    const MESI_IT = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                     'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
    const now = new Date();
    const d = meseSel
      ? new Date(Number(meseSel.slice(0, 4)), Number(meseSel.slice(5, 7)) - 1, 1)
      : new Date(now.getFullYear(), now.getMonth(), 1);
    const periodo = `${MESI_IT[d.getMonth()]}_${d.getFullYear()}`;
    api.get(`/ped/vault/${encodeURIComponent(clienteAzienda)}/${periodo}`)
      .then(r => setVaultFiles({ ...r.data, periodo }))
      .catch(() => setVaultFiles(null));
  }, [clienteAzienda, meseSel]);


  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [publishing, setPublishing] = useState(false);
  const [filtroPosting, setFiltroPosting] = useState(''); // '' | 'pronta' | 'da_risolvere' | 'programmata' | 'pubblicata'

  // Partizione futuri/passati (data "oggi" dal browser: affidabile e indipendente dal server).
  const pad2 = (n) => String(n).padStart(2, '0');
  const _now = new Date();
  const oggiStr = `${_now.getFullYear()}-${pad2(_now.getMonth() + 1)}-${pad2(_now.getDate())}`;
  const dataOf = (r) => (r.data_pubblicazione || '').slice(0, 10);
  const isFuturo = (r) => { const d = dataOf(r); return !d || d >= oggiStr; };
  const futuri = righe.filter(isFuturo);
  const passati = righe.filter((r) => !isFuturo(r));
  const passatiDaRisolvere = passati.filter((r) => r.stato_posting === 'da_risolvere').length;

  // Mesi selezionabili in "In programma": quelli presenti nei futuri + i prossimi 3
  // (corrente, +1, +2) garantiti, così puoi pianificare anche un mese ancora vuoto.
  const mesiFuturi = (() => {
    const set = new Set();
    for (let i = 0; i < 3; i++) { const d = new Date(_now.getFullYear(), _now.getMonth() + i, 1); set.add(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`); }
    futuri.forEach(r => { const k = dataOf(r).slice(0, 7); if (k) set.add(k); });
    return [...set].sort();
  })();
  const meseLabel = (k) => { const [y, m] = k.split('-'); return `${MESI_LBL[parseInt(m, 10) - 1]} '${y.slice(2)}`; };
  // In "In programma" applichiamo il filtro mese (vuoto = tutti i futuri, incl. le bozze senza data).
  const futuriMese = meseSel ? futuri.filter(r => dataOf(r).slice(0, 7) === meseSel) : futuri;
  const righeSez = sezione === 'archivio' ? passati : futuriMese;

  // Pubblicabile = stato di posting 'pronta' (approvato + media + profilo + data futura).
  const isPublishable = (r) => r.stato_posting === 'pronta';
  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selezionabili = righeSez.filter(isPublishable);
  const righeView = filtroPosting ? righeSez.filter(r => r.stato_posting === filtroPosting) : righeSez;
  const selectAll = () => setSelected(new Set(selezionabili.map(r => r.id)));

  // Conteggi per la barra guardrail (sezione corrente).
  const conteggi = righeSez.reduce((a, r) => { a[r.stato_posting] = (a[r.stato_posting]||0)+1; return a; }, {});

  // Archivio raggruppato per mese (YYYY-MM), più recente prima.
  const MESI_ARCH = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
  const gruppiArchivio = () => {
    const map = {};
    righeView.forEach(r => { const k = dataOf(r).slice(0, 7); (map[k] = map[k] || []).push(r); });
    return Object.keys(map).sort().reverse().map(k => {
      const [y, m] = k.split('-');
      return { key: k, label: `${MESI_ARCH[parseInt(m, 10) - 1]} ${y}`, righe: map[k] };
    });
  };
  const toggleMese = (k) => setMesiChiusi(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const pubblicaSelezionati = async () => {
    const ids = [...selected].filter(id => selezionabili.some(r => r.id === id));
    if (!ids.length) return;
    if (!window.confirm(`Pubblicare ${ids.length} contenut${ids.length===1?'o':'i'} su PostProxy?`)) return;
    setPublishing(true);
    try {
      const r = await api.post('/ped/publish', { ped_ids: ids });
      const res = r.data.results || [];
      const ok = res.filter(x => x.ok).length;
      const fail = res.filter(x => !x.ok && !x.skipped).length;
      const skip = res.filter(x => x.skipped).length;
      const motivi = [...new Set(res.filter(x => (x.skipped || !x.ok) && x.error).map(x => x.error))];
      const refresh = await api.get(pedQuery());
      setRighe(refresh.data);
      setSelected(new Set()); setSelectMode(false);
      if (ok > 0) {
        success(`Programmati ${ok}${fail ? ` · ${fail} falliti` : ''}${skip ? ` · ${skip} saltati` : ''}`);
      } else {
        error(`Niente programmato${motivi.length ? `: ${motivi[0]}` : ''}`);
      }
    } catch {}
    setPublishing(false);
  };

  // Programma TUTTE le righe pronte in un colpo (batch, commit per-riga lato server).
  const programmaTutte = async () => {
    const ids = selezionabili.map(r => r.id);
    if (!ids.length) return;
    if (!window.confirm(`Programmare su PostProxy ${ids.length} contenut${ids.length===1?'o':'i'} pront${ids.length===1?'o':'i'}?`)) return;
    setPublishing(true);
    try {
      const r = await api.post('/ped/publish', { ped_ids: ids });
      const res = r.data.results || [];
      const ok = res.filter(x => x.ok).length;
      const fail = res.filter(x => !x.ok && !x.skipped).length;
      const skip = res.filter(x => x.skipped).length;
      const motivi = [...new Set(res.filter(x => (x.skipped || !x.ok) && x.error).map(x => x.error))];
      const refresh = await api.get(pedQuery()); setRighe(refresh.data);
      setSelected(new Set()); setSelectMode(false);
      if (ok > 0) success(`Programmati ${ok}${fail ? ` · ${fail} falliti` : ''}${skip ? ` · ${skip} saltati` : ''}`);
      else error(`Niente programmato${motivi.length ? `: ${motivi[0]}` : ''}`);
    } catch {}
    setPublishing(false);
  };

  // Recupero a un click per una riga 'Da risolvere' (scaduta o fallita).
  const riprogrammaRiga = async (r) => {
    const oggi = new Date().toISOString().slice(0,10);
    const passata = r.data_pubblicazione && r.data_pubblicazione.slice(0,10) < oggi;
    const body = {};
    if (passata) {
      const nd = window.prompt('Nuova data di pubblicazione (YYYY-MM-DD):', oggi);
      if (!nd) return;
      body.data_pubblicazione = nd;
    } else if (!window.confirm('Riprogrammare questo contenuto su PostProxy?')) return;
    try {
      const res = await api.post(`/ped/${r.id}/riprogramma`, body);
      const refresh = await api.get(pedQuery()); setRighe(refresh.data);
      if (res.data && res.data.ok) success('Riprogrammato');
      else error((res.data && res.data.error) || 'Riprogrammazione non riuscita');
    } catch { error('Errore in riprogrammazione'); }
  };

  const resetPub = async (r) => {
    if (!window.confirm('Azzerare lo stato di pubblicazione di questa riga? (non rimuove il post su PostProxy, ma la rende di nuovo pubblicabile)')) return;
    try {
      await api.delete(`/ped/${r.id}/pubblicazione`);
      const res = await api.get(pedQuery());
      setRighe(res.data);
      success('Stato pubblicazione azzerato');
    } catch {}
  };

  // Solo quelli che si vedono nella lista: il numero sul pulsante deve corrispondere a ciò che hai davanti.
  const inReview = righeView.filter(r => r.stato === 'In Review');

  // Contratto a contenuti: quanti ne mancano nel mese mostrato (o nel corrente), per ogni cliente.
  // Si ricalcola quando cambiano i contenuti, così il contatore segue le modifiche.
  const meseCopertura = meseSel || `${_now.getFullYear()}-${pad2(_now.getMonth() + 1)}`;
  const [coperture, setCoperture] = useState({});
  useEffect(() => {
    api.get(`/ped/copertura?mese=${meseCopertura}`, { silent: true })
      .then(r => setCoperture(Object.fromEntries((Array.isArray(r.data) ? r.data : []).map(c => [c.cliente_id, c]))))
      .catch(() => setCoperture({}));
  }, [meseCopertura, righe]);
  const coperturaCliente = selectedCliente ? coperture[selectedCliente] : null;
  // Migrazione applicata (il campo esiste) ma contratto non impostato: invito a farlo
  const contrattoDaImpostare = !!clienteSel && 'contenuti_quota' in clienteSel && !clienteSel.contenuti_quota
    && (puoPubblicare || user?.ruolo === 'direttore');
  const approvaTutti = async () => {
    if (!inReview.length) return;
    if (!window.confirm(`Approvare ${inReview.length} contenut${inReview.length===1?'o':'i'} in review?`)) return;
    for (const r of inReview) { try { await api.patch(`/ped/${r.id}/approva`, { approvato: true }); } catch {} }
    const res = await api.get(pedQuery());
    setRighe(res.data);
    success(`${inReview.length} contenuti approvati`);
  };

  // PDF da mostrare al cliente: il mese scelto per intero, oppure tutti i prossimi. Mai gli scartati.
  const scaricaPdf = () => {
    const base = meseSel ? righe.filter(r => dataOf(r).slice(0, 7) === meseSel) : futuri;
    const daMostrare = base.filter(r => r.stato !== 'Scartato');
    if (!daMostrare.length) { error(meseSel ? `Nessun contenuto in ${meseLabel(meseSel)}` : 'Nessun contenuto in programma'); return; }
    generatePedPDF({ cliente: clienteAzienda || clienteNome, righe: daMostrare, mese: meseSel });
  };

  // Duplica tutti i contenuti del mese scelto (o del corrente) sul mese successivo, come base di partenza
  const meseDaCopiare = meseSel || `${_now.getFullYear()}-${pad2(_now.getMonth() + 1)}`;
  const copiaMese = async () => {
    const [y, m] = meseDaCopiare.split('-').map(Number);
    const dest = new Date(y, m, 1);
    const a = `${dest.getFullYear()}-${pad2(dest.getMonth() + 1)}`;
    if (!window.confirm(`Copiare i contenuti di ${meseLabel(meseDaCopiare)} su ${meseLabel(a)}? Le copie ripartono da "Idea", senza media né approvazioni.`)) return;
    try {
      const r = await api.post('/ped/duplica_mese', { cliente_id: selectedCliente, da: meseDaCopiare, a });
      const res = await api.get(pedQuery()); setRighe(res.data);
      success(r.data.creati ? `${r.data.creati} contenuti copiati su ${meseLabel(a)}` : 'Nessun contenuto da copiare in quel mese');
      if (r.data.creati) setMeseSel(a);
    } catch { /* toast globale */ }
  };

  const [refreshing, setRefreshing] = useState(false);
  const refreshStatus = async () => {
    if (!selectedCliente) return;
    setRefreshing(true);
    try {
      const r = await api.post(`/ped/refresh_status?cliente_id=${selectedCliente}`);
      const res = await api.get(pedQuery());
      setRighe(res.data);
      success(r.data.updated > 0 ? `${r.data.updated} stati aggiornati da PostProxy` : 'Stati già allineati');
    } catch {}
    setRefreshing(false);
  };

  const [syncing, setSyncing] = useState(false);
  const syncMedia = async () => {
    if (!selectedCliente) return;
    setSyncing(true);
    try {
      const r = await api.post(`/ped/autolink_media?cliente_id=${selectedCliente}`);
      const res = await api.get(pedQuery());
      setRighe(res.data);
      success(r.data.linked > 0 ? `${r.data.linked} contenuti collegati da _APPROVED` : 'Nessun nuovo media da collegare');
    } catch {}
    setSyncing(false);
  };

  // Modifica parziale: invia SOLO i campi cambiati (PATCH) e sostituisce la riga con
  // quella restituita dal server (stato ricalcolato, auto-avanzamento incluso).
  // Così non si sovrascrivono le modifiche fatte nel frattempo da un collega.
  const patchRiga = async (r, patch) => {
    setRighe(rs => rs.map(x => x.id === r.id ? { ...x, ...patch } : x));   // ottimistico
    try {
      const res = await api.patch(`/ped/${r.id}`, patch);
      setRighe(rs => rs.map(x => x.id === r.id ? { ...x, ...res.data } : x));
      return true;
    } catch {
      const res = await api.get(pedQuery()).catch(() => null);           // riallinea
      if (res) setRighe(res.data);
      return false;
    }
  };

  // Aggiornamento automatico ogni 30s (solo con la scheda visibile e fuori dalla selezione)
  useLiveRefresh(() => {
    if (!selectedCliente || selectMode || publishing) return;
    api.get(pedQuery(), { silent: true }).then(r => setRighe(r.data)).catch(() => {});
  }, 30000, !!selectedCliente);

  const toggleCopy = (r) => patchRiga(r, { copy_ok: !r.copy_ok });
  const toggleVisual = (r) => patchRiga(r, { visual_ok: !r.visual_ok });

  // Data/ora di pubblicazione inline (post, reel, caroselli). L'ora è usata dallo
  // scheduler PostProxy; se assente si applica il default per tipo (post 12:00).
  const patchQuando = (r, patch) => patchRiga(r, patch);

  const approva = async (r) => {
    try {
      await api.patch(`/ped/${r.id}/approva`, { approvato: true });
      const res = await api.get(pedQuery());
      setRighe(res.data);
      success('Contenuto approvato');
    } catch(e) { /* errore notificato dal toast globale */ }
  };

  const deletePed = async (rid) => {
    if (!window.confirm('Eliminare questa riga?')) return;
    await api.delete(`/ped/${rid}`);
    setRighe(prev => prev.filter(r => r.id !== rid));
    success('Riga eliminata');
  };

  // Apri la pagina di dettaglio/modifica del contenuto
  const openEdit = (r) => navigate(`/ped/contenuto/${r.id}`);
  const openNew = (dateStr) => navigate(`/ped/contenuto/nuovo?cliente_id=${selectedCliente}${dateStr ? `&data=${dateStr}` : ''}`);

  // Sposta un contenuto su un altro giorno (drag&drop nel calendario)
  const moveData = async (r, newDate) => {
    if (!newDate || (r.data_pubblicazione || '').slice(0,10) === newDate) return;
    if (await patchRiga(r, { data_pubblicazione: newDate })) success('Contenuto spostato');
  };

  // Mese target del calendario: il mese scelto (o il corrente se "Tutti").
  const calMonth = meseSel
    ? new Date(Number(meseSel.slice(0, 4)), Number(meseSel.slice(5, 7)) - 1, 1)
    : new Date(_now.getFullYear(), _now.getMonth(), 1);

  const hasAds = righe.some(r => r.ads_campagna || Number(r.spending_speso) || Number(r.spending_budget));
  const COLS = colsFor(hasAds);
  const COL_HEADERS = headersFor(hasAds);
  const MINW = minWFor(hasAds);

  const totaleSpeso = righeSez.reduce((a, r) => a + (Number(r.spending_speso)||0), 0);
  const totaleBudget = righeSez.reduce((a, r) => a + (Number(r.spending_budget)||0), 0);
  // riga con la modale media aperta, sempre allineata allo stato corrente
  const modalStory = mediaFor ? (righe.find(x => x.id === mediaFor.id) || mediaFor) : null;

  // Render di UNA riga (densa) — riusato da "In programma" e da ogni mese di "Archivio".
  const renderRiga = (r, idx, total) => (
    <div key={r.id} className="eden-ped-row" style={{
      display:'grid', gridTemplateColumns: COLS, minWidth: MINW, alignItems:'center', padding:'9px 14px', minHeight:56,
      borderBottom: idx < total - 1 ? '1px solid var(--border)' : 'none',
      background: selected.has(r.id) ? 'var(--green-dim)' : undefined,
    }}>
      {/* ID */}
      <div style={{ display:'flex', alignItems:'center', gap:6, minWidth:0 }}>
        {selectMode && (isPublishable(r)
          ? <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} style={{ cursor:'pointer', accentColor:'var(--green)', flexShrink:0 }}/>
          : <span style={{ width:13, flexShrink:0 }}/>)}
        {r.id_post
          ? <span title={r.id_post} style={{ fontSize:10.5, fontFamily:'monospace', fontWeight:600, color:'var(--cyan)', background:'var(--cyan-dim)', padding:'2px 6px', borderRadius:5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%' }}>{r.id_post}</span>
          : <span style={{ fontSize:11, color:'var(--text-dim)' }}>—</span>}
      </div>
      {/* Titolo / Hook */}
      <div onClick={() => { if (!selectMode) openEdit(r); }} style={{ cursor: selectMode ? 'default' : 'pointer', minWidth:0, paddingRight:10 }}>
        <div style={{ fontSize:13.5, fontWeight:600, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.titolo_post}</div>
        {r.hook && <div style={{ fontSize:12, color:'var(--text-dim)', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.hook}</div>}
        <div style={{ marginTop:3 }}><SegnaliContenuto r={r} /></div>
      </div>
      {/* Formato */}
      <div><FormatTag formato={r.formato} /></div>
      {/* Data · ora (affiancate) */}
      <div style={{ display:'flex', gap:5, alignItems:'center', paddingRight:8 }} onClick={e => e.stopPropagation()}>
        <input type="date" aria-label="Data di pubblicazione" className="eden-dt" value={r.data_pubblicazione || ''}
          style={{ flex:'1 1 auto', minWidth:0 }}
          onChange={e => patchQuando(r, { data_pubblicazione: e.target.value || null })} />
        <input type="time" aria-label="Orario di pubblicazione" className="eden-dt"
          title="Vuoto = orario di default per tipo (post 12:00)" value={hhmm(r.ora_pubblicazione)}
          style={{ width:88, flexShrink:0 }}
          onChange={e => patchQuando(r, { ora_pubblicazione: e.target.value || null })} />
      </div>
      {/* Copy · Visual */}
      {renderCheck(r)}
      {/* Stato */}
      <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
        {renderStato(r)}
        {!PRODUZIONE.includes(r.stato) && r.pub_stato && puoPubblicare && (
          <button onClick={() => resetPub(r)} title="Annulla programmazione / azzera stato pubblicazione" aria-label="Azzera stato pubblicazione"
            className="eden-icon-btn" style={{ width:24, height:24, color:'var(--text-dim)' }}
            onMouseEnter={e=>e.currentTarget.style.color='var(--red)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}>
            <X size={13}/>
          </button>
        )}
      </div>
      {/* Ads / Spending */}
      {hasAds && (() => {
        const speso = Number(r.spending_speso||0), budget = Number(r.spending_budget||0);
        if (!r.ads_campagna && !speso && !budget) return <span style={{ fontSize:12, color:'var(--text-dim)' }}>—</span>;
        return (
          <div style={{ fontSize:11, color:'var(--text-muted)', minWidth:0 }}>
            {r.ads_campagna && <div style={{ color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.ads_campagna}</div>}
            <div>€{speso.toFixed(0)} / €{budget.toFixed(0)}</div>
          </div>
        );
      })()}
      {/* Azioni */}
      <div style={{ display:'flex', gap:5, alignItems:'center' }}>
        <button onClick={() => setMediaFor(r)} title={`Media (${(r.media_url||[]).length})`} className="eden-icon-btn"
          style={{ width:30, height:30, color:(r.media_url||[]).length?'var(--cyan)':'var(--text-dim)' }}>
          <ImageIcon size={15}/>
          {(r.media_url||[]).length>0 &&
            <span style={{ position:'absolute', top:1, right:1, minWidth:13, height:13, padding:'0 2px', borderRadius:7, background:'var(--cyan)', color:'var(--on-accent)', fontSize:8.5, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center' }}>{(r.media_url||[]).length}</span>}
        </button>
        <button onClick={() => openEdit(r)} title="Apri / modifica" aria-label="Apri" className="eden-icon-btn" style={{ width:30, height:30 }}><Pencil size={15}/></button>
        {puoPubblicare && <button onClick={() => deletePed(r.id)} title="Elimina" aria-label="Elimina" className="eden-icon-btn" style={{ width:30, height:30, color:'var(--text-dim)' }}
          onMouseEnter={e=>e.currentTarget.style.color='var(--red)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-dim)'}><Trash2 size={15}/></button>}
      </div>
    </div>
  );

  // Spunte Copy / Visual: chi ha finito la sua parte lo segna con un tocco.
  const renderCheck = (r) => (
    <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
      {[['copy_ok', 'Copy', toggleCopy], ['visual_ok', 'Visual', toggleVisual]].map(([k, label, fn]) => (
        <button key={k} onClick={() => fn(r)} aria-pressed={!!r[k]} title={r[k] ? `${label} pronto — tocca per annullare` : `Segna ${label.toLowerCase()} pronto`}
          style={{ display:'inline-flex', alignItems:'center', gap:4, height:26, minHeight:0, padding:'0 9px', borderRadius:999, cursor:'pointer',
            fontSize:11.5, fontWeight:600, fontFamily:'inherit',
            border:`1px solid ${r[k] ? 'transparent' : 'var(--border)'}`,
            background: r[k] ? 'var(--green-dim)' : 'transparent', color: r[k] ? 'var(--green)' : 'var(--text-muted)' }}>
          {r[k] ? <Check size={12} strokeWidth={2.6}/> : <Clock size={12}/>}{label}
        </button>
      ))}
    </div>
  );

  // Stato: un solo badge coerente (editoriale in produzione, altrimenti posting)
  const renderStato = (r) => PRODUZIONE.includes(r.stato) ? (
    <>
      <StatoBadge stato={r.stato}/>
      {r.stato === 'In Review' && !r.approvato_da && puoPubblicare && (
        <button onClick={(e) => { e.stopPropagation(); approva(r); }} className="eden-btn is-sm is-success" style={{ height:26 }}>Approva</button>
      )}
    </>
  ) : (
    <>
      <PostingBadge stato={r.stato_posting} manca={r.posting_manca}/>
      {r.stato_posting === 'pubblicata' && <InsightsMini ins={r.pub_insights}/>}
      {r.stato_posting === 'da_risolvere' && puoPubblicare && (
        <button onClick={(e) => { e.stopPropagation(); riprogrammaRiga(r); }} className="eden-btn is-sm" style={{ height:26, color:'var(--cyan)' }}>Riprogramma</button>
      )}
    </>
  );

  // Telefono: ogni contenuto è una scheda (niente tabella da scorrere di lato)
  const renderCard = (r) => {
    const sel = selected.has(r.id);
    const pubblicabile = isPublishable(r);
    return (
      <div key={r.id} className="eden-list-row" style={{ alignItems:'flex-start', gap:12, background: sel ? 'var(--green-dim)' : undefined, opacity: selectMode && !pubblicabile ? 0.5 : 1 }}
        onClick={() => { if (selectMode) { if (pubblicabile) toggleSel(r.id); } else openEdit(r); }}>
        <div onClick={e => { e.stopPropagation(); if (!selectMode) setMediaFor(r); }} title="Media">
          <Thumb r={r} size={56}/>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'var(--text-dim)', marginBottom:3 }}>
            <FormatTag formato={r.formato}/>
            <span style={{ marginLeft:'auto', fontWeight:600, color: (r.data_pubblicazione || '').slice(0,10) === oggiStr ? 'var(--cyan)' : 'var(--text-muted)', whiteSpace:'nowrap' }}>
              {r.data_pubblicazione ? giornoLabel(r.data_pubblicazione) : 'Senza data'}{r.ora_pubblicazione ? ` · ${hhmm(r.ora_pubblicazione)}` : ''}
            </span>
          </div>
          <div style={{ fontSize:14.5, fontWeight:600, color:'var(--text)', lineHeight:1.3 }}>{r.titolo_post}</div>
          {r.hook && <div style={{ fontSize:12.5, color:'var(--text-dim)', marginTop:2, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{r.hook}</div>}
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginTop:9 }}>
            {renderStato(r)}
            <SegnaliContenuto r={r} />
          </div>
          {!selectMode && <div style={{ marginTop:8 }}>{renderCheck(r)}</div>}
        </div>
      </div>
    );
  };

  const HeaderRow = () => (
    <div style={{ display:'grid', gridTemplateColumns: COLS, background:'var(--bg2)', padding:'10px 14px', borderBottom:'1px solid var(--border)', minWidth: MINW }}>
      {COL_HEADERS.map((h, i) => (
        <span key={i} style={{ fontSize:11, color:'var(--text-dim)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</span>
      ))}
    </div>
  );

  const mesiRow = sezione === 'programma' && (
    <div className="eden-scroll-row" role="group" aria-label="Mese">
      <button className={`eden-chip${meseSel === '' ? ' is-on' : ''}`} onClick={() => setMeseSel('')} title="Tutti i contenuti futuri">Tutti</button>
      {mesiFuturi.map(k => (
        <button key={k} className={`eden-chip${meseSel === k ? ' is-on' : ''}`} onClick={() => setMeseSel(k)}>{meseLabel(k)}</button>
      ))}
    </div>
  );

  return (
    <div className="eden-page">
      {/* Intestazione: una sola azione principale, il resto nel menu ⋯ */}
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:12, marginBottom:18, flexWrap:'wrap' }}>
        <div style={{ minWidth:0 }}>
          <h1 style={{ margin:0 }}>Piano Editoriale</h1>
          <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:4 }}>
            {clienteNome ? <>Post, reel e caroselli di <b style={{ color:'var(--text)', fontWeight:600 }}>{clienteAzienda || clienteNome}</b></> : 'Scegli un cliente per vedere il suo piano.'}
          </div>
        </div>
        {selectedCliente && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {puoPubblicare && inReview.length > 0 && (
              <button onClick={approvaTutti} className="eden-btn is-success" title="Approva tutti i contenuti in review">
                <Check size={15}/>{isMobile ? `Approva ${inReview.length}` : `Approva tutti (${inReview.length})`}
              </button>
            )}
            <MoreMenu items={[
              { label: meseSel ? `Scarica PDF di ${meseLabel(meseSel)} per il cliente` : 'Scarica PDF per il cliente', icon: FileDown, onClick: scaricaPdf },
              { label: 'Rubriche', icon: Repeat, onClick: () => setRubricheOpen(true) },
              { label: 'Voce del brand', icon: Settings2, onClick: () => setVoceOpen(true) },
              ...(puoPubblicare ? [
                { label: selectMode ? 'Annulla selezione' : 'Seleziona e pubblica…', icon: Send, onClick: () => { setSelectMode(m => !m); setSelected(new Set()); } },
                { label: refreshing ? 'Aggiorno…' : 'Aggiorna stato da PostProxy', icon: RefreshCw, onClick: refreshStatus, disabled: refreshing },
              ] : []),
              { label: syncing ? 'Sincronizzo…' : 'Collega media approvati', icon: Link2, onClick: syncMedia, disabled: syncing },
              { label: `Copia ${meseLabel(meseDaCopiare)} sul mese dopo`, icon: CopyIcon, onClick: copiaMese },
            ]}/>
            <button onClick={() => openNew()} className="eden-btn is-primary">
              <Plus size={16}/>{isMobile ? 'Nuovo' : 'Nuovo contenuto'}
            </button>
          </div>
        )}
      </div>

      {/* Selettore cliente */}
      <ClientePicker clienti={clienti} selected={selectedCliente} badge={c => <BadgeCopertura cop={coperture[c.id]} />}
        onSelect={c => setSelectedCliente(c.id)} />

      {/* Sezione (In programma / Archivio) + mese + vista */}
      {selectedCliente && (
        <>
          <div className="eden-toolbar">
            <div className="eden-seg" role="group" aria-label="Sezione">
              {[{ k:'programma', label:'In programma' }, { k:'archivio', label:'Archivio' }].map(x => (
                <button key={x.k} className={sezione === x.k ? 'is-on' : ''}
                  onClick={() => { setSezione(x.k); setFiltroPosting(''); if (x.k === 'archivio') setVista('lista'); }}>
                  {x.label}
                  {x.k === 'archivio' && passati.length > 0 && <span style={{ fontSize:11, color:'var(--text-dim)', fontWeight:600 }}>{passati.length}</span>}
                  {x.k === 'archivio' && passatiDaRisolvere > 0 && <span title={`${passatiDaRisolvere} da risolvere`} style={{ width:7, height:7, borderRadius:4, background:'var(--red)' }}/>}
                </button>
              ))}
            </div>
            {!isMobile && <div style={{ flex:'1 1 auto', minWidth:0 }}>{mesiRow}</div>}
            {sezione === 'programma' && (
              <div className="eden-seg" role="group" aria-label="Vista" style={{ marginLeft:'auto' }}>
                {[{ k:'lista', label:'Lista', Icon:List }, { k:'calendario', label:'Calendario', Icon:CalendarDays }].map(({ k, label, Icon }) => (
                  <button key={k} className={vista === k ? 'is-on' : ''} onClick={() => setVista(k)} aria-label={label}>
                    <Icon size={15}/>{!isMobile && label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isMobile && mesiRow && <div style={{ marginBottom:14 }}>{mesiRow}</div>}
        </>
      )}

      {/* Contratto: quanti contenuti mancano nel mese / nelle settimane */}
      {selectedCliente && sezione === 'programma' && (coperturaCliente
        ? <CoperturaContratto cop={coperturaCliente} onNew={openNew} />
        : contrattoDaImpostare && (
          <button type="button" className="eden-btn is-sm" onClick={() => navigate(`/clienti/${selectedCliente}`)}
            style={{ marginBottom:14, color:'var(--text-muted)' }}>
            <FileSignature size={14}/> Imposta quanti contenuti prevede il contratto
          </button>
        ))}

      {/* Barra guardrail: stato di posting a colpo d'occhio + azione batch (componente condiviso) */}
      {selectedCliente && (
        <PostingGuardrail conteggi={conteggi} filtro={filtroPosting} onFiltro={setFiltroPosting}
          pronte={puoPubblicare ? selezionabili.length : 0} onProgrammaTutte={programmaTutte} publishing={publishing} />
      )}

      {/* Vault links: PED / PDP / PRE-PED */}
      {vaultFiles && (vaultFiles.ped || vaultFiles.pdp || vaultFiles.preped) && (
        <div className="eden-scroll-row" style={{ gap:8, marginBottom:14 }}>
          {[
            { key:'ped',    Icon:FileBarChart2, label:'PED' },
            { key:'pdp',    Icon:ClipboardList, label:'Piano di Produzione' },
            { key:'preped', Icon:FileText,      label:'PRE-PED' },
          ].filter(x => vaultFiles[x.key]).map(x => (
            <a key={x.key}
              href={`/api/ped/vault/${encodeURIComponent(clienteAzienda)}/${vaultFiles.periodo}/download/${encodeURIComponent(vaultFiles[x.key])}`}
              target="_blank" rel="noreferrer"
              className="eden-btn is-sm" style={{ textDecoration:'none', color:'var(--text-muted)' }}
            >
              <x.Icon size={14}/> {x.label}
            </a>
          ))}
        </div>
      )}

      {/* Ads/Spending summary */}
      {(totaleSpeso > 0 || totaleBudget > 0) && (
        <div style={{ display:'flex', gap:12, marginBottom:16 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 16px', fontSize:12 }}>
            <span style={{ color:'var(--text-muted)' }}>Spending totale: </span>
            <span style={{ fontWeight:700, color:'var(--text)' }}>€{totaleSpeso.toFixed(2)}</span>
            <span style={{ color:'var(--text-muted)' }}> / €{totaleBudget.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Barra pubblicazione manuale (Lista + Calendario) */}
      {selectedCliente && selectMode && (
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', background:'var(--green-dim)', borderRadius:12, padding:'10px 12px', marginBottom:12, position:'sticky', top:'calc(var(--topbar-h) + 8px)', zIndex:5 }}>
          <span style={{ fontSize:12.5, color:'var(--text)' }}>
            <b>{[...selected].filter(id => selezionabili.some(r=>r.id===id)).length}</b> selezionati · {selezionabili.length} pubblicabili (Approvati + media)
          </span>
          <button onClick={selectAll} className="eden-btn is-sm">Seleziona tutti</button>
          <button onClick={pubblicaSelezionati} disabled={publishing || selected.size===0} className="eden-btn is-primary is-sm" style={{ marginLeft:'auto' }}>
            {publishing ? 'Pubblico…' : `Pubblica selezionati (${[...selected].filter(id => selezionabili.some(r=>r.id===id)).length})`}
          </button>
        </div>
      )}

      {/* Tabella (In programma) / Calendario / Archivio per mese */}
      {!selectedCliente ? (
        <EmptyState icon={FileText} title="Scegli un cliente" hint="Tocca uno dei clienti qui sopra per vedere il suo Piano Editoriale." />
      ) : loading ? (
        <ListSkeleton rows={6} />
      ) : (sezione === 'programma' && vista === 'calendario') ? (
        <PedCalendario month={calMonth} righe={righeView} onMove={moveData} onOpen={openEdit} onNew={openNew}
          selectMode={selectMode} selected={selected} toggleSel={toggleSel} isPublishable={isPublishable}/>
      ) : righeView.length === 0 ? (
        <EmptyState icon={sezione === 'archivio' ? Archive : List}
          title={sezione === 'archivio' ? 'Archivio vuoto' : 'Niente in programma'}
          hint={filtroPosting
            ? `Nessun contenuto in stato "${(POSTING_STYLE[filtroPosting]||{}).label || filtroPosting}".`
            : sezione === 'archivio' ? 'I contenuti con data passata finiscono qui, raggruppati per mese.'
            : 'Nessun contenuto futuro. Usa "Nuovo contenuto" per aggiungerne uno.'} />
      ) : sezione === 'archivio' ? (
        // ARCHIVIO: un blocco collassabile per mese (più recente prima)
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {gruppiArchivio().map(g => {
            const aperto = !mesiChiusi.has(g.key);
            return (
              <div key={g.key} className="eden-list">
                <button onClick={() => toggleMese(g.key)}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'12px 14px', minHeight:0, background:'var(--bg2)', border:'none', borderBottom: aperto ? '1px solid var(--border)' : 'none', cursor:'pointer', color:'var(--text)', textAlign:'left', fontFamily:'inherit' }}>
                  <ChevronDown size={15} style={{ flexShrink:0, transform: aperto ? 'none' : 'rotate(-90deg)', transition:'transform 0.15s', color:'var(--text-muted)' }}/>
                  <span style={{ fontSize:13.5, fontWeight:700 }}>{g.label}</span>
                  <span style={{ fontSize:11.5, color:'var(--text-muted)' }}>· {g.righe.length} contenut{g.righe.length===1?'o':'i'}</span>
                </button>
                {aperto && (isMobile ? g.righe.map(renderCard) : (
                  <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
                    <HeaderRow/>
                    {g.righe.map((r, idx) => renderRiga(r, idx, g.righe.length))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        // IN PROGRAMMA: lista piatta (solo contenuti futuri + senza data)
        <div className="eden-list">
          {isMobile ? righeView.map(renderCard) : (
            <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
              <HeaderRow/>
              {righeView.map((r, idx) => renderRiga(r, idx, righeView.length))}
            </div>
          )}
        </div>
      )}

      {/* Gestione media del contenuto (componente condiviso) */}
      <MediaManager row={modalStory} clienteId={selectedCliente} onPatch={patchQuando} onClose={() => setMediaFor(null)} />
      <RubricheModal open={rubricheOpen} clienteId={selectedCliente} clienteNome={clienteNome} onClose={() => setRubricheOpen(false)}
        onGenerated={() => api.get(pedQuery()).then(r => setRighe(r.data)).catch(() => {})} />
      <VoceBrandModal open={voceOpen} clienteId={selectedCliente} onClose={() => setVoceOpen(false)} />
    </div>
  );
}
